import React from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import { StaticQuery, graphql } from 'gatsby';

const SEO = ({ title, desc, banner, pathname, article }) => (
  <StaticQuery
    query={query}
    render={({
      site: {
        buildTime,
        siteMetadata: {
          defaultTitle,
          titleAlt,
          shortName,
          author,
          siteLanguage,
          logo,
          siteUrl,
          pathPrefix,
          defaultDescription,
          defaultBanner,
          twitter,
        },
      },
    }) => {
      const seo = {
        title: title || defaultTitle,
        description: defaultDescription || desc,
        image: `${siteUrl}${banner || defaultBanner}`,
        url: `${siteUrl}${pathname || '/'}`,
      };
      const realPrefix = pathPrefix === '/' ? '' : pathPrefix;
      let schemaOrgJSONLD = [
        {
          '@context': 'http://schema.org',
          '@type': 'WebSite',
          '@id': siteUrl,
          url: siteUrl,
          name: defaultTitle,
          alternateName: titleAlt || '',
        },
      ];
      if (article) {
        schemaOrgJSONLD = [
          {
            '@context': 'http://schema.org',
            '@type': 'BlogPosting',
            '@id': seo.url,
            url: seo.url,
            name: title,
            alternateName: titleAlt || '',
            headline: title,
            image: {
              '@type': 'ImageObject',
              url: seo.image,
            },
            description: seo.description,
            datePublished: buildTime,
            dateModified: buildTime,
            author: {
              '@type': 'Person',
              name: author,
            },
            publisher: {
              '@type': 'Organization',
              name: author,
              logo: {
                '@type': 'ImageObject',
                url: siteUrl + realPrefix + logo,
              },
            },
            isPartOf: siteUrl,
            mainEntityOfPage: {
              '@type': 'WebSite',
              '@id': siteUrl,
            },
          },
        ];
      }
      return (
        <>
          <Helmet title={seo.title}>
            <html lang={siteLanguage} />
            <meta name="description" content={seo.description} />
            <meta name="image" content="https://cookle.asymmetry.life/logo/icon-512x512.png" />
            <meta name="apple-mobile-web-app-title" content={shortName} />
            <meta name="application-name" content={shortName} />
            <script type="application/ld+json">
              {JSON.stringify(schemaOrgJSONLD)}
            </script>
            <link rel="canonical" href={seo.url} />


            {/* OpenGraph  */}
            <meta property="og:url" content={seo.url} />
            <meta property="og:type" content={article ? 'article' : null} />
            <meta property="og:title" content={seo.title} />
            <meta property="og:description" content={seo.description} />
            <meta property="og:image" content="https://cookle.asymmetry.life/icons/static/icons/ios-launchimage-1024-768.png" />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:creator" content={twitter} />
            <meta name="twitter:title" content={seo.title} />
            <meta name="twitter:description" content={seo.description} />
            <meta name="twitter:image" content="https://cookle.asymmetry.life/icons/static/icons/ios-launchimage-1024-768.png" />
            <meta
              name="thumbnail"
              content="https://cookle.asymmetry.life/icons/icon-512x512.png"
            />


            <link
              rel="mask-icon"
              href="https://cookle.asymmetry.life/icons/safari-pinned-tab.svg"
              color="#fd9535"
            />
            <meta name="application-name" content="https://cookle.asymmetry.life" />
            <meta
              name="msapplication-square70x70logo"
              content="https://cookle.asymmetry.life/icons/small.jpg"
            />
            <meta
              name="msapplication-square150x150logo"
              content="https://cookle.asymmetry.life/icons/medium.jpg"
            />
            <meta
              name="msapplication-wide310x150logo"
              content="https://cookle.asymmetry.life/icons/wide.jpg"
            />
            <meta
              name="msapplication-square310x310logo"
              content="https://cookle.asymmetry.life/icons/large.jpg"
            />
            <meta name="msapplication-TileColor" content="#fd9535" />
            <meta name="theme-color" content="#fd9535" />
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-status-bar-style" content="#fd9535" />

            <link
              rel="apple-touch-startup-image"
              media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
              href="https://cookle.asymmetry.life/icons/ios-launchimage-1242-2688.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="https://cookle.asymmetry.life/icons/ios-launchimage-828-1792.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
              href="https://cookle.asymmetry.life/icons/ios-launchimage-1125-2436.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="(device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
              href="https://cookle.asymmetry.life/icons/ios-launchimage-1242-2208.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="https://cookle.asymmetry.life/icons/ios-launchimage-750-1334.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="https://cookle.asymmetry.life/icons/ios-launchimage-2048-2732.png"
            />

            <link
              rel="apple-touch-startup-image"
              media="(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="https://cookle.asymmetry.life/icons/ios-launchimage-1668-2388.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="https://cookle.asymmetry.life/icons/ios-launchimage-1668-2224.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="https://cookle.asymmetry.life/icons/ios-launchimage-1536-2048.png"
            />
            <link rel="shortcut icon" href="https://cookle.asymmetry.life/icons/favicon.ico" />
            <link rel="icon" type="image/png" sizes="16x16" href="https://cookle.asymmetry.life/icons/icon-16x16.png" />
            <link rel="icon" type="image/png" sizes="32x32" href="https://cookle.asymmetry.life/icons/icon-32x32.png" />

          </Helmet>
        </>
      );
    }}
  />
);
//   }
// }

export default SEO;

SEO.propTypes = {
  title: PropTypes.string,
  desc: PropTypes.string,
  banner: PropTypes.string,
  pathname: PropTypes.string,
  article: PropTypes.bool,
};

SEO.defaultProps = {
  title: null,
  desc: null,
  banner: null,
  pathname: null,
  article: false,
};

const query = graphql`
  query SEO {
    site {
      buildTime(formatString: "YYYY-MM-DD")
      siteMetadata {
        defaultTitle: title
        titleAlt
        shortName
        author
        siteLanguage
        logo
        siteUrl: url
        pathPrefix
        defaultDescription: description
        defaultBanner: banner
        twitter
      }
    }
  }
`;
